import React, { useState, useRef, useEffect } from "react";
import ActionDropdown from "./ActionDropdown";
import SortDropdown from "./SortDropdown";
import FlightLIsting from "./FlightLIsting";
import { MyRequestItem, MyRequestParams, MyRequestResponse } from "./types";
import { getUserLoadRequests } from "apis/myRequestsService";
import { useGlobalLoader } from "../../../src/contexts/GlobalLoaderContext";
import YourRequestFilter from "./YourRequestFilter";
import SecondHeader from "../../assets/images/top-header-2.png";

interface YourRequestProps {
  onShowHistory: () => void;
}

interface FilterState {
  status?: string;
}

interface FlightDetails {
  departure: string;
  status: string;
  timeLeft: string;
  terminal: string;
  gate: string;
  aircraft: string;
}

interface Location {
  code: string;
  city: string;
  time: string;
}

interface FlightStatus {
  text: string;
  color: string;
}

export interface Flight {
  id: number;
  airline: string;
  logoImg: string;
  flightNo: string;
  date: string;
  status: FlightStatus;
  from: Location;
  to: Location;
  duration: string;
  details: FlightDetails;
}

const YourRequest: React.FC<YourRequestProps> = ({ onShowHistory }) => {
  const { showLoader, hideLoader } = useGlobalLoader();
  // const [flightsDetails, setFlightsDetails] = useState<MyRequestResponse[]>([]);
  const [flightsDetails, setFlightsDetails] = useState<MyRequestResponse>({
    items: [],
    totalCount: 0,
    page: 1,
    pageSize: 10, // Default value, modify as needed
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [actionIsOpen, setActionIsOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("departure");
  const [sortOrder, setSortOrder] = useState("asc");
  const [status, setStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // Adjust as needed
  const [showFilter, setShowFilter] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [clearSelectAll, setClearSelectAll] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    status: "",
  });

  const handleFilterChange = (filters: FilterState) => {
    setStatus(filters.status || ""); // Update the status in parent
    setSelectedFilters(filters); // Store the selected filters
    setShowFilter(false); // Close the filter panel after applying
    if (filters.status === "Expired") {
      setCurrentPage(1);
    }
  };

  const handleActionClick = (action: string) => {
    console.log("Button clicked:", action);
    setSelectedAction(action);
    // Reset the action after processing
    setTimeout(() => setSelectedAction(null), 100); // Small delay to allow execution
  };

  const toggleFilter = () => setShowFilter((prev) => !prev);

  const fetchLoadRequests = async () => {
    showLoader();
    setLoading(true);
    setError(null);

    try {
      const params: MyRequestParams = {
        page: currentPage,
        size: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
        status: status,
      };
      const data = await getUserLoadRequests(params);
      if (status === "Expired" && data.items.length === 0 && currentPage > 1) {
        setCurrentPage(1);
        return;
      }
      setFlightsDetails(data);
      console.log("Fetched load requests:", data);
    } catch (err) {
      console.error("Error fetching load requests:", err);
      setError("Failed to fetch load requests");
    } finally {
      setLoading(false);
      setTimeout(() => hideLoader(), 500);
    }
  };

  // ✅ Call fetchLoadRequests inside useEffect
  useEffect(() => {
    fetchLoadRequests();
  }, [status, currentPage, sortBy, sortOrder]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdown(null); // Close the dropdown
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(flightsDetails.totalCount / pageSize)
    ) {
      setCurrentPage(newPage);
    }
    // Reset the "Select All" checkbox
    setClearSelectAll(true);

    // Set it back to false after a brief delay (to allow re-rendering)
    setTimeout(() => setClearSelectAll(false), 0);
  };

  // Function to generate page numbers to display
  // Function to generate page numbers to display
  const getPageNumbers = () => {
    const totalPages = Math.ceil(flightsDetails.totalCount / pageSize);

    // If 5 or fewer pages, show all pages
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Always include current page, and try to have 2 pages before and after
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    // Adjust if we're at the end
    if (endPage === totalPages) {
      startPage = Math.max(1, endPage - 4);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  };

  return (
    <>
      <div className="">
        <section className="flex flex-wrap gap-4 items-center justify-between h-[125.5px] bg-cover  p-6">
          <p className="text-[#333333] text-lg font-semibold">Enquiries</p>
          <div className="flex flex-wrap items-center gap-2">
            {/* Filters Button */}
            <div className="relative">
              <button
                onClick={toggleFilter}
                className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg"
              >
                <i className="st-filter text-[#000000]"></i>
                <p className="font-medium text-[#000000] text-sm">Filters</p>
              </button>
              {showFilter && (
                <div className="absolute left-0 mt-2 w-full">
                  <YourRequestFilter
                    activeFilters={selectedFilters}
                    onClose={() => setShowFilter(false)}
                    onFilterChange={handleFilterChange}
                  />
                </div>
              )}
            </div>
            <button
              onClick={onShowHistory}
              className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg"
            >
              <i className="st-previous text-[#000000]"></i>
              <p className="font-medium text-[#000000] text-sm">History</p>
            </button>
            <div className="relative inline-block">
              <button
                id="dropdownDefaultButton"
                onClick={() => setActionIsOpen((prev) => !prev)}
                className="flex items-center gap-2 p-3 bg-[#FFFFFF] rounded-lg"
                type="button"
              >
                <i className="st-more text-[#000000] text-xs"></i>
                <p className="font-medium text-[#000000] text-sm">Actions</p>
              </button>
              <ActionDropdown
                actionIsOpen={actionIsOpen}
                setActionIsOpen={setActionIsOpen}
                onActionClick={handleActionClick}
              />
            </div>
            <div
              id="dropdown-2"
              className="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-56"
            >
              <ul
                className="px-4 text-sm text-gray-700"
                aria-labelledby="dropdownDefaultButton"
              >
                <li>
                  <a href="#" className="flex items-center gap-2 py-2 mt-2">
                    <i className="st-thumbpin text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">PIN Flight</p>
                  </a>
                </li>
                <li>
                  <a
                    data-modal-target="default-modal"
                    data-modal-toggle="default-modal"
                    href="#"
                    className="flex items-center gap-2 py-2"
                  >
                    <i className="st-reload text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm cursor-pointer">
                      Request Update
                    </p>
                  </a>
                </li>

                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-warning text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">
                      Report Inaccurate Load
                    </p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-enable text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">
                      Enable Status Update
                    </p>
                  </a>
                </li>
                <li>
                  <a
                    data-modal-target="default-modal-2"
                    data-modal-toggle="default-modal-2"
                    href="#"
                    className="flex items-center gap-2 py-2"
                  >
                    <i className="st-share text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Share Request</p>
                  </a>
                </li>
                <li>
                  <a
                    data-modal-target="default-modal-1"
                    data-modal-toggle="default-modal-1"
                    href="#"
                    className="flex items-center gap-2 py-2"
                  >
                    <i className="st-delete text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Delete Request</p>
                  </a>
                </li>
              </ul>
            </div>
            <div className="relative">
              <button
                id="dropdownDefaultButton"
                data-dropdown-toggle="dropdown-3"
                className="flex items-center gap-2 p-4 bg-[#FFFFFF] rounded-lg"
                type="button"
                onClick={() => setIsSortDropdownOpen((prev) => !prev)}
              >
                <i className="st-options text-[#000000] text-xs"></i>
              </button>

              {/* SortDropdown component */}
              <SortDropdown
                isOpen={isSortDropdownOpen}
                setIsOpen={setIsSortDropdownOpen}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                sortBy={sortBy}
                sortOrder={sortOrder} //
              />
            </div>
            <div
              id="dropdown-3"
              className="z-10 hidden bg-white divide-y divide-gray-100 rounded-lg shadow w-56"
            >
              <ul
                className="px-4 text-sm text-gray-700"
                aria-labelledby="dropdownDefaultButton"
              >
                <p className="py-4 border-b border-gray-200 font-semibold text-grease-100">
                  Sort By
                </p>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2 mt-2">
                    <i className="st-priority text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Priority</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-arrow-slant text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Departure Time</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-route text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Route</p>
                  </a>
                </li>
                <li>
                  <a href="#" className="flex items-center gap-2 py-2">
                    <i className="st-flight-slim text-lightgray-150 text-sm"></i>
                    <p className="text-lightgray-200 text-sm">Flight Number</p>
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </section>
        <div className="px-4">
          <FlightLIsting
            flights={flightsDetails}
            action={selectedAction}
            clearSelectAll={clearSelectAll}
            fetchLoadRequests={fetchLoadRequests}
            loading={loading}
          />
        </div>
        <section>
          <nav>
            <ul className="flex items-center justify-center my-6 gap-3 text-sm ">
              <li>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`flex items-center justify-center w-8 h-8 leading-tight ${
                    currentPage === 1
                      ? "text-gray-300 border border-gray-300 cursor-not-allowed"
                      : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
                  } rounded-md`}
                >
                  <span className="sr-only">Previous</span>
                  <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
                </button>
              </li>

              {getPageNumbers().map((pageNumber) => (
                <li key={pageNumber}>
                  <button
                    onClick={() => handlePageChange(pageNumber)}
                    className={`flex items-center justify-center w-8 h-8 rounded-md ${
                      currentPage === pageNumber
                        ? "text-white border border-primaryblue-50 bg-primaryblue-50"
                        : "text-primaryblue-50 border border-primaryblue-50"
                    }`}
                  >
                    {pageNumber}
                  </button>
                </li>
              ))}

              <li>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={
                    currentPage >=
                    Math.ceil(flightsDetails.totalCount / pageSize)
                  }
                  className={`flex items-center justify-center w-8 h-8 ${
                    currentPage >=
                    Math.ceil(flightsDetails.totalCount / pageSize)
                      ? "text-gray-300 border border-gray-300 cursor-not-allowed"
                      : "text-primaryblue-50 border border-primaryblue-50 cursor-pointer"
                  } rounded-md`}
                >
                  <span className="sr-only">Next</span>
                  <i className="st-arrow-right text-xl font-semibold"></i>
                </button>
              </li>
            </ul>
          </nav>
        </section>
      </div>
    </>
  );
};

export default YourRequest;
